import { HttpClient } from '@angular/common/http'
import {
    AfterViewChecked,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'

import {
    SubcontractorTrainingParticipantSession,
    SubcontractorTrainingParticipantSummary,
    SubcontractorTrainingPublicInvite,
    SubcontractorTrainingPublicModule,
    SubcontractorTrainingSubmitModuleResponse,
    SubcontractorTrainingValidateAnswerResponse,
} from '../../shared/data-models/subcontractor-training'

type WizardStep = 'loading' | 'error' | 'resume-pick' | 'welcome' | 'video' | 'quiz' | 'complete'

interface QuestionState {
    selectedAnswer: string
    isCorrect: boolean | null
    correctAnswer: string
    explanation: string
}

interface YouTubePlayer {
    destroy(): void
    getCurrentTime(): number
    getDuration(): number
    getPlayerState(): number
}

declare global {
    interface Window {
        onYouTubeIframeAPIReady?: () => void
        YT?: {
            Player: new (
                elementId: string,
                config: {
                    videoId: string
                    playerVars?: Record<string, number | string>
                    events?: {
                        onReady?: (event: { target: YouTubePlayer }) => void
                        onStateChange?: (event: { data: number; target: YouTubePlayer }) => void
                    }
                },
            ) => YouTubePlayer
            PlayerState: {
                ENDED: number
                PLAYING: number
            }
        }
    }
}

let youtubeApiPromise: Promise<void> | null = null

function loadYouTubeIframeApi(): Promise<void> {
    if (window.YT?.Player) {
        return Promise.resolve()
    }

    if (!youtubeApiPromise) {
        youtubeApiPromise = new Promise(resolve => {
            const previousReady = window.onYouTubeIframeAPIReady
            window.onYouTubeIframeAPIReady = () => {
                previousReady?.()
                resolve()
            }

            const script = document.createElement('script')
            script.src = 'https://www.youtube.com/iframe_api'
            document.head.appendChild(script)
        })
    }

    return youtubeApiPromise
}

@Component({
    selector: 'app-subcontractor-training-wizard',
    templateUrl: './subcontractor-training-wizard.component.html',
    styleUrls: ['./subcontractor-training-wizard.component.css'],
    standalone: true,
    imports: [FormsModule],
})
export class SubcontractorTrainingWizardComponent implements OnInit, AfterViewChecked, OnDestroy {
    private http = inject(HttpClient)
    private route = inject(ActivatedRoute)

    @ViewChild('youtubePlayerHost') youtubePlayerHost?: ElementRef<HTMLDivElement>

    private static readonly WATCH_THRESHOLD = 0.92
    private static readonly SEEK_TOLERANCE_SECONDS = 3
    private static readonly PROGRESS_INTERVAL_MS = 500

    step: WizardStep = 'loading'
    errorMessage = ''
    invite?: SubcontractorTrainingPublicInvite
    resumableParticipants: SubcontractorTrainingParticipantSummary[] = []
    participantId = ''
    currentModuleIndex = 0
    firstName = ''
    lastName = ''
    videoAcknowledged = false
    questionStates: Record<string, QuestionState> = {}
    isSubmitting = false
    submitMessage = ''

    videoWatchRequirementMet = false
    videoWatchProgressPercent = 0
    videoTrackingUnavailable = false

    private youtubePlayer?: YouTubePlayer
    private progressTimer?: ReturnType<typeof setInterval>
    private pendingPlayerInit = false
    private maxWatchedSeconds = 0
    private videoDurationSeconds = 0
    private activeVideoModuleId = ''

    ngOnInit() {
        const token = this.route.snapshot.paramMap.get('token') ?? ''
        if (!token) {
            this.step = 'error'
            this.errorMessage = 'This training link is invalid.'
            return
        }

        this.http.get<SubcontractorTrainingPublicInvite>(`api/SubcontractorTrainingPublic/GetByToken?token=${encodeURIComponent(token)}`).subscribe({
            next: invite => {
                this.invite = invite
                const storedParticipantId = this.getStoredParticipantId()
                if (storedParticipantId) {
                    this.loadParticipantProgress(storedParticipantId)
                    return
                }
                this.loadResumableParticipants()
            },
            error: () => {
                this.step = 'error'
                this.errorMessage = 'This training link is invalid or could not be found.'
            },
        })
    }

    private loadResumableParticipants() {
        this.http
            .get<SubcontractorTrainingParticipantSummary[]>(
                `api/SubcontractorTrainingPublic/GetInviteParticipants?token=${encodeURIComponent(this.token)}`,
            )
            .subscribe({
                next: participants => {
                    this.resumableParticipants = (participants ?? []).filter(p => !p.isCompleted)
                    this.step = this.resumableParticipants.length > 0 ? 'resume-pick' : 'welcome'
                },
                error: () => {
                    this.resumableParticipants = []
                    this.step = 'welcome'
                },
            })
    }

    resumeAs(participantId: string) {
        if (!participantId) return
        this.participantId = participantId
        this.storeParticipantSession()
        this.loadParticipantProgress(participantId)
    }

    startAsNewPerson() {
        this.clearStoredParticipantId()
        this.participantId = ''
        this.firstName = ''
        this.lastName = ''
        this.currentModuleIndex = 0
        this.resetModuleState()
        this.step = 'welcome'
    }

    registerAnotherEmployee() {
        this.clearStoredParticipantId()
        this.participantId = ''
        this.firstName = ''
        this.lastName = ''
        this.currentModuleIndex = 0
        this.resetModuleState()
        this.step = 'welcome'
    }

    ngAfterViewChecked() {
        if (!this.pendingPlayerInit || this.step !== 'video' || !this.youtubePlayerHost) {
            return
        }

        this.pendingPlayerInit = false
        void this.initVideoPlayer()
    }

    ngOnDestroy() {
        this.destroyVideoPlayer()
    }

    get token() {
        return this.route.snapshot.paramMap.get('token') ?? ''
    }

    get currentModule(): SubcontractorTrainingPublicModule | undefined {
        return this.invite?.modules[this.currentModuleIndex]
    }

    get canProceedFromWelcome() {
        return this.firstName.trim().length > 0 && this.lastName.trim().length > 0
    }

    get canProceedFromVideo() {
        return this.videoAcknowledged && (this.videoWatchRequirementMet || this.videoTrackingUnavailable)
    }

    get canAcknowledgeVideo() {
        return this.videoWatchRequirementMet || this.videoTrackingUnavailable
    }

    get allQuestionsCorrect() {
        const module = this.currentModule
        if (!module) return false
        return module.questions.every(question => this.questionStates[question.id]?.isCorrect === true)
    }

    get moduleProgressLabel() {
        if (!this.invite) return ''
        return `Module ${this.currentModuleIndex + 1} of ${this.invite.modules.length}`
    }

    get submitQuizButtonLabel() {
        if (!this.invite) return 'Submit Quiz'

        const remainingIncomplete = this.invite.modules.filter(module => !module.isCompleted).length
        if (this.invite.modules.length === 1) {
            return 'Submit Quiz'
        }
        if (remainingIncomplete > 1) {
            return 'Submit and Proceed to Next Module'
        }
        return 'Submit and Complete Training'
    }

    get currentYouTubeVideoId() {
        return this.getYouTubeVideoId(this.currentModule?.youTubeUrl ?? '')
    }

    continueFromWelcome() {
        if (!this.canProceedFromWelcome) return

        this.http
            .post<SubcontractorTrainingParticipantSession>(
                `api/SubcontractorTrainingPublic/StartParticipant?token=${encodeURIComponent(this.token)}`,
                {
                    firstName: this.firstName.trim(),
                    lastName: this.lastName.trim(),
                },
            )
            .subscribe({
                next: session => {
                    this.applyParticipantSession(session)
                    this.resetModuleState()
                    if (session.modules.every(x => x.isCompleted)) {
                        this.step = 'complete'
                        return
                    }
                    this.currentModuleIndex = this.findStartingModuleIndex(session.modules)
                    this.step = 'video'
                    this.scheduleVideoPlayerInit()
                },
                error: () => {
                    this.errorMessage = 'Unable to start your training session. Please try again.'
                },
            })
    }

    continueFromVideo() {
        if (!this.canProceedFromVideo) return
        this.destroyVideoPlayer()
        this.initializeQuestionStates()
        this.step = 'quiz'
    }

    selectAnswer(questionId: string, selectedAnswer: string) {
        const state = this.questionStates[questionId] ?? {
            selectedAnswer: '',
            isCorrect: null,
            correctAnswer: '',
            explanation: '',
        }
        state.selectedAnswer = selectedAnswer
        this.questionStates[questionId] = state

        this.http
            .post<SubcontractorTrainingValidateAnswerResponse>(
                `api/SubcontractorTrainingPublic/ValidateAnswer?token=${encodeURIComponent(this.token)}`,
                { questionId, selectedAnswer },
            )
            .subscribe({
                next: result => {
                    state.isCorrect = result.isCorrect
                    state.correctAnswer = result.correctAnswer
                    state.explanation = result.explanation
                    this.questionStates[questionId] = { ...state }
                },
                error: () => {
                    state.isCorrect = false
                    this.questionStates[questionId] = { ...state }
                },
            })
    }

    submitQuiz() {
        if (!this.invite || !this.currentModule || !this.participantId || !this.allQuestionsCorrect || this.isSubmitting) return

        this.isSubmitting = true
        this.submitMessage = ''

        const payload = {
            participantId: this.participantId,
            moduleId: this.currentModule.moduleId,
            answers: this.currentModule.questions.map(question => ({
                questionId: question.id,
                selectedAnswer: this.questionStates[question.id]?.selectedAnswer ?? '',
            })),
        }

        this.http
            .post<SubcontractorTrainingSubmitModuleResponse>(
                `api/SubcontractorTrainingPublic/SubmitModule?token=${encodeURIComponent(this.token)}`,
                payload,
            )
            .subscribe({
                next: result => {
                    this.isSubmitting = false
                    if (!result.success) {
                        this.submitMessage = result.message || 'Please correct all answers before submitting.'
                        return
                    }

                    this.currentModule!.isCompleted = true
                    const nextIndex = this.findNextIncompleteModuleIndex(this.currentModuleIndex + 1)
                    if (nextIndex === -1 || this.invite!.modules.every(x => x.isCompleted)) {
                        this.destroyVideoPlayer()
                        this.clearStoredParticipantId()
                        this.step = 'complete'
                        return
                    }

                    this.currentModuleIndex = nextIndex
                    this.resetModuleState()
                    this.step = 'video'
                    this.scheduleVideoPlayerInit()
                },
                error: () => {
                    this.isSubmitting = false
                    this.submitMessage = 'Unable to submit your training results. Please try again.'
                },
            })
    }

    getYouTubeVideoId(url: string): string | null {
        if (!url?.trim()) return null

        const trimmed = url.trim()
        if (trimmed.startsWith('PLACEHOLDER_')) return null

        const watchMatch = trimmed.match(/[?&]v=([^&]+)/)
        if (watchMatch?.[1]) return watchMatch[1]

        const shortMatch = trimmed.match(/youtu\.be\/([^?&]+)/)
        if (shortMatch?.[1]) return shortMatch[1]

        const embedMatch = trimmed.match(/embed\/([^?&]+)/)
        if (embedMatch?.[1]) return embedMatch[1]

        return null
    }

    private scheduleVideoPlayerInit() {
        this.pendingPlayerInit = true
    }

    private async initVideoPlayer() {
        const module = this.currentModule
        const host = this.youtubePlayerHost?.nativeElement
        if (!module || !host) return

        this.destroyVideoPlayer()
        this.resetVideoTrackingState()

        const videoId = this.getYouTubeVideoId(module.youTubeUrl)
        if (!videoId) {
            this.videoTrackingUnavailable = true
            return
        }

        this.activeVideoModuleId = module.moduleId

        try {
            await loadYouTubeIframeApi()

            if (this.currentModule?.moduleId !== module.moduleId || this.step !== 'video') {
                return
            }

            host.innerHTML = ''
            const playerElement = document.createElement('div')
            playerElement.id = `subcon-youtube-player-${module.moduleId}`
            host.appendChild(playerElement)

            this.youtubePlayer = new window.YT!.Player(playerElement.id, {
                videoId,
                playerVars: {
                    rel: 0,
                    modestbranding: 1,
                    playsinline: 1,
                },
                events: {
                    onReady: () => this.onPlayerReady(),
                    onStateChange: event => this.onPlayerStateChange(event.data),
                },
            })
        } catch {
            this.videoTrackingUnavailable = true
        }
    }

    private onPlayerReady() {
        const duration = this.youtubePlayer?.getDuration() ?? 0
        if (duration > 0) {
            this.videoDurationSeconds = duration
        }

        this.startProgressTracking()
    }

    private onPlayerStateChange(state: number) {
        if (state === window.YT?.PlayerState.PLAYING) {
            this.startProgressTracking()
            return
        }

        if (state === window.YT?.PlayerState.ENDED) {
            this.updateWatchProgress(true)
            this.stopProgressTracking()
        }
    }

    private startProgressTracking() {
        if (this.progressTimer) return

        this.progressTimer = setInterval(() => {
            this.updateWatchProgress(false)
        }, SubcontractorTrainingWizardComponent.PROGRESS_INTERVAL_MS)
    }

    private stopProgressTracking() {
        if (!this.progressTimer) return
        clearInterval(this.progressTimer)
        this.progressTimer = undefined
    }

    private updateWatchProgress(forceComplete: boolean) {
        if (!this.youtubePlayer) return

        const duration = this.videoDurationSeconds > 0 ? this.videoDurationSeconds : this.youtubePlayer.getDuration()
        if (duration > 0) {
            this.videoDurationSeconds = duration
        }

        const currentTime = this.youtubePlayer.getCurrentTime()
        if (currentTime > this.maxWatchedSeconds + SubcontractorTrainingWizardComponent.SEEK_TOLERANCE_SECONDS) {
            this.videoWatchProgressPercent = duration > 0 ? Math.round((this.maxWatchedSeconds / duration) * 100) : 0
            return
        }

        if (currentTime > this.maxWatchedSeconds) {
            this.maxWatchedSeconds = currentTime
        }

        if (duration > 0) {
            this.videoWatchProgressPercent = Math.min(100, Math.round((this.maxWatchedSeconds / duration) * 100))
        }

        const watchRatio = duration > 0 ? this.maxWatchedSeconds / duration : 0
        if (forceComplete || watchRatio >= SubcontractorTrainingWizardComponent.WATCH_THRESHOLD) {
            this.videoWatchRequirementMet = true
            this.videoWatchProgressPercent = 100
            this.stopProgressTracking()
        }
    }

    private resetVideoTrackingState() {
        this.videoWatchRequirementMet = false
        this.videoWatchProgressPercent = 0
        this.videoTrackingUnavailable = false
        this.maxWatchedSeconds = 0
        this.videoDurationSeconds = 0
        this.activeVideoModuleId = ''
    }

    private destroyVideoPlayer() {
        this.stopProgressTracking()
        this.youtubePlayer?.destroy()
        this.youtubePlayer = undefined

        if (this.youtubePlayerHost?.nativeElement) {
            this.youtubePlayerHost.nativeElement.innerHTML = ''
        }
    }

    private findStartingModuleIndex(modules: SubcontractorTrainingPublicModule[]) {
        const firstIncomplete = modules.findIndex(x => !x.isCompleted)
        return firstIncomplete === -1 ? 0 : firstIncomplete
    }

    private findNextIncompleteModuleIndex(startIndex: number) {
        if (!this.invite) return -1
        for (let index = startIndex; index < this.invite.modules.length; index++) {
            if (!this.invite.modules[index].isCompleted) return index
        }
        return -1
    }

    private resetModuleState() {
        this.destroyVideoPlayer()
        this.videoAcknowledged = false
        this.resetVideoTrackingState()
        this.questionStates = {}
        this.submitMessage = ''
    }

    private initializeQuestionStates() {
        this.questionStates = {}
        this.currentModule?.questions.forEach(question => {
            this.questionStates[question.id] = {
                selectedAnswer: '',
                isCorrect: null,
                correctAnswer: '',
                explanation: '',
            }
        })
    }

    private loadParticipantProgress(participantId: string) {
        this.http
            .get<SubcontractorTrainingParticipantSession>(
                `api/SubcontractorTrainingPublic/GetParticipantProgress?token=${encodeURIComponent(this.token)}&participantId=${encodeURIComponent(participantId)}`,
            )
            .subscribe({
                next: session => {
                    this.applyParticipantSession(session)
                    if (session.modules.every(x => x.isCompleted)) {
                        this.step = 'complete'
                        return
                    }
                    this.currentModuleIndex = this.findStartingModuleIndex(session.modules)
                    this.resetModuleState()
                    this.step = 'video'
                    this.scheduleVideoPlayerInit()
                },
                error: () => {
                    this.clearStoredParticipantId()
                    this.step = 'welcome'
                },
            })
    }

    private applyParticipantSession(session: SubcontractorTrainingParticipantSession) {
        this.participantId = session.participantId
        this.firstName = session.firstName
        this.lastName = session.lastName
        if (this.invite) {
            this.invite.modules = session.modules
            this.invite.welcomeText = session.welcomeText
            this.invite.companyName = session.companyName
        }
        this.storeParticipantSession()
    }

    private participantStorageKey() {
        return `subcon-participant-${this.token}`
    }

    private getStoredParticipantId() {
        return localStorage.getItem(this.participantStorageKey()) ?? ''
    }

    private storeParticipantSession() {
        if (this.participantId) {
            localStorage.setItem(this.participantStorageKey(), this.participantId)
        }
    }

    private clearStoredParticipantId() {
        localStorage.removeItem(this.participantStorageKey())
    }
}
